"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from "react";
import Link from "next/link";
import { Terminal, User, Mail, Link as LinkIcon, Copy, ClipboardPaste } from "lucide-react";
import { playThud } from "@/utils/audio";
import { cycleAboutImage } from "@/utils/aboutImage";
import { useNeumorphicTheme } from "@/hooks/useNeumorphicTheme";

// --- CUSTOM GITHUB ICON ---
const GithubIcon = t0 => {
  const $ = _c(4);
  const {
    size: t1
  } = t0;
  const size = t1 === undefined ? 16 : t1;
  let t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <path d="M15 22v-4a4.8 4.8 0 0 0-1-3.5c3 0 6-2 6-5.5.08-1.25-.27-2.48-1-3.5.28-1.15.28-2.35 0-3.5 0 0-1 0-3 1.5-2.64-.5-5.36-.5-8 0C6 2 5 2 5 2c-.3 1.15-.3 2.35 0 3.5A5.403 5.403 0 0 0 4 9c0 3.5 3 5.5 6 5.5-.39.49-.68 1.05-.85 1.65-.17.6-.22 1.23-.15 1.85v4" />;
    t3 = <path d="M9 18c-4.51 2-5-2-7-2" />;
    $[0] = t2;
    $[1] = t3;
  } else {
    t2 = $[0];
    t3 = $[1];
  }
  let t4;
  if ($[2] !== size) {
    t4 = <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{t2}{t3}</svg>;
    $[2] = size;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  return t4;
};
export default function CustomContextMenu({
  dict
}: {
  dict?: Record<string, string>;
}) {
  const isNeumorphic = useNeumorphicTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({
    x: 0,
    y: 0
  });
  const [currentLocale, setCurrentLocale] = useState("en");

  // Track exactly what element was right-clicked to enable precise pasting
  const [targetElement, setTargetElement] = useState<HTMLElement | null>(null);
  useEffect(() => {
    requestAnimationFrame(() => {
      setCurrentLocale(window.location.pathname.split('/')[1] || "en");
    });
    const handleContextMenu = (e: MouseEvent) => {
      e.preventDefault();
      const safeX = Math.min(e.clientX, window.innerWidth - 220);
      const safeY = Math.min(e.clientY, window.innerHeight - 300); // Increased buffer for taller menu

      setPosition({
        x: safeX,
        y: safeY
      });
      setTargetElement(e.target as HTMLElement);
      setIsOpen(true);

      // TRIGGER HEAVY BASS DROP
      playThud();
    };
    const handleClick = () => {
      if (isOpen) setIsOpen(false);
    };
    const handleScroll = () => {
      if (isOpen) setIsOpen(false);
    };
    window.addEventListener("contextmenu", handleContextMenu);
    window.addEventListener("click", handleClick);
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => {
      window.removeEventListener("contextmenu", handleContextMenu);
      window.removeEventListener("click", handleClick);
      window.removeEventListener("scroll", handleScroll);
    };
  }, [isOpen]);
  const handleCopy = () => {
    const selectedText = window.getSelection()?.toString();
    if (selectedText) navigator.clipboard.writeText(selectedText);
    setIsOpen(false);
  };
  const handlePaste = async () => {
    try {
      const text = await navigator.clipboard.readText();
      const target = targetElement as HTMLInputElement | HTMLTextAreaElement;

      // If you right-clicked directly inside an input or textarea
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) {
        target.focus();

        // Temporarily convert email inputs to text inputs so the browser allows pasting
        const isEmail = target.getAttribute('type') === 'email';
        if (isEmail) target.setAttribute('type', 'text');

        // Natively inject the text exactly where the cursor is resting
        target.setRangeText(text, target.selectionStart || 0, target.selectionEnd || 0, 'end');

        // Revert it back to an email input instantly
        if (isEmail) target.setAttribute('type', 'email');
      }
    } catch {
      console.warn("Paste permission denied or not supported.");
    }
    setIsOpen(false);
  };
  const copyUrl = () => {
    navigator.clipboard.writeText(window.location.href);
    setIsOpen(false);
  };
  if (!isOpen) return null;
  return <div className={`fixed z-[9999] bg-white border-4 border-black flex flex-col w-[200px] context-menu-wrapper ${isNeumorphic ? "brutalist-shadow" : "brutalist-shadow-dark"}`} style={{
    top: `${position.y}px`,
    left: `${position.x}px`
  }} dir={currentLocale === "ar" ? "rtl" : "ltr"}>
      {/* Menu Header */}
      <div className="bg-black text-white px-3 py-2 text-xs font-black uppercase tracking-widest flex items-center gap-2 cursor-default">
        <Terminal size={14} /> {dict?.systemMenu || "System_Menu"}
      </div>

      {/* Windows 11 Style Action Bar */}
      <div className="flex border-b-4 border-black bg-white">
        <button onClick={handleCopy} className="flex-1 flex flex-col items-center justify-center py-3 border-r-4 border-black rtl:border-r-0 rtl:border-l-4 rtl:border-black hover:bg-black hover:text-white transition-colors group">
          <Copy size={20} className="mb-1" />
          <span className="text-[10px] font-black uppercase tracking-widest">{dict?.copy || "Copy"}</span>
        </button>

        <button onClick={handlePaste} className="flex-1 flex flex-col items-center justify-center py-3 hover:bg-black hover:text-white transition-colors group">
          <ClipboardPaste size={20} className="mb-1" />
          <span className="text-[10px] font-black uppercase tracking-widest">{dict?.paste || "Paste"}</span>
        </button>
      </div>

      {/* Menu Actions */}
      <button onClick={copyUrl} className="flex items-center gap-3 w-full text-left rtl:text-right px-4 py-3 text-sm font-black uppercase border-b-4 border-black hover:bg-black hover:text-white transition-colors text-black">
        <LinkIcon size={16} /> {dict?.copyUrl || "Copy URL"}
      </button>

      <Link href={`/${currentLocale}/about`} onClick={() => cycleAboutImage()} className="flex items-center gap-3 w-full text-left rtl:text-right px-4 py-3 text-sm font-black uppercase border-b-4 border-black hover:bg-black hover:text-white transition-colors text-black">
        <User size={16} /> {dict?.specs || "Specs"}
      </Link>

      <a href="https://github.com/samybit/portfolio" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 w-full text-left rtl:text-right px-4 py-3 text-sm font-black uppercase border-b-4 border-black hover:bg-black hover:text-white transition-colors text-black">
        {/* Replaced Lucide Github with custom inline SVG */}
        <GithubIcon size={16} /> {dict?.source || "Source"}
        <span className="sr-only">{dict?.newTab || " (opens in a new tab)"}</span>
      </a>

      <Link href={`/${currentLocale}#contact`} className="flex items-center gap-3 w-full text-left rtl:text-right px-4 py-3 text-sm font-black uppercase hover:bg-black hover:text-white transition-colors text-black">
        <Mail size={16} /> {dict?.contact || "Contact"}
      </Link>
    </div>;
}